import { Button, Card, MonoLabel } from '@/design-system';
import { formatDayTimeShort, formatTime } from '@/shared/lib';

import { surveyCopy } from '../copy';
import type { NodeId } from '../engine';
import type { CompletionReport } from '../model/completion';
import type { QueuedPhoto } from '../model/photo-queue';

export interface CompletionPanelProps {
    report: CompletionReport;
    /** `not_started` · `in_progress` · `completed` · `submitted` */
    status: string;
    completedAt: string | null;
    submittedAt: string | null;
    online: boolean;
    pending: { operations: number; photos: number };
    /** De foto's die nog naar de server moeten, met per foto wat er speelt. */
    photoQueue: readonly QueuedPhoto[];
    /** Verplichte foto's die hier staan maar nog niet op de server: zie `requiredPhotosOnTheWay`. */
    requiredPhotosOnTheWay: number;
    busy: 'complete' | 'submit' | null;
    error: string | null;
    onOpenNode: (nodeId: NodeId) => void;
    onComplete: () => void;
    onSubmit: () => void;
    onSyncNow: () => void;
    onRetryPhoto: (clientUuid: string) => void;
    onSkipPhoto: (clientUuid: string) => void;
    onClose: () => void;
}

/**
 * Afronden en indienen, in de middenkolom van de opname - geen blokkerende
 * dialoog: de inspecteur kan elk open onderdeel met één tik openen en terug.
 *
 * - **Afronden** kan altijd, ook offline, en blokkeert nooit.
 * - **Indienen** is de onomkeerbare stap: alleen met verbinding, en pas als de
 *   wijzigingen van deze opname op de server staan - de server dient in wat hij
 *   kent. Open vragen en foto's houden het niet tegen: wat open staat komt niet
 *   in het rapport, en een foto die nog onderweg is gaat erna mee (het concept
 *   wordt dan herbouwd). Het scherm zegt beide, zonder te blokkeren.
 */
export function CompletionPanel({
    report,
    status,
    completedAt,
    submittedAt,
    online,
    pending,
    photoQueue,
    requiredPhotosOnTheWay,
    busy,
    error,
    onOpenNode,
    onComplete,
    onSubmit,
    onSyncNow,
    onRetryPhoto,
    onSkipPhoto,
    onClose,
}: CompletionPanelProps) {
    const copy = surveyCopy.completion;
    const submitted = status === 'submitted';
    const canSubmit = !submitted && online && pending.operations === 0 && busy === null;

    return (
        <div className="flex flex-col gap-[14px]" data-testid="completion">
            <div>
                <MonoLabel size={10} as="div" className="mb-[5px] tracking-[0.08em]">
                    {copy.eyebrow}
                </MonoLabel>
                <h1 className="m-0 font-[family-name:var(--font-display)] text-[25px] leading-[1.2] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                    {submitted ? copy.submittedTitle : copy.title}
                </h1>
                <p className="m-0 mt-[6px] text-[13px] text-[var(--text-tertiary-safe)]">
                    {submitted && submittedAt
                        ? copy.submittedAt(formatDayTimeShort(submittedAt))
                        : status === 'completed' && completedAt
                          ? copy.completedAt(formatDayTimeShort(completedAt))
                          : copy.notCompleted}
                </p>
            </div>

            <Card padding="md">
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-primary)]">
                    {copy.answered(report.questionsAnswered, report.questionsVisible)}
                </p>
                {report.photosMissing > 0 ? (
                    <p className="m-0 mt-1 text-[14px] leading-[1.5] text-[var(--warn-text)]">
                        {copy.photosMissing(report.photosMissing)}
                    </p>
                ) : null}
                {report.amountsMissing.length > 0 ? (
                    <p className="m-0 mt-1 text-[14px] leading-[1.5] text-[var(--warn-text)]">
                        {copy.amountsMissing(report.amountsMissing.length)}
                    </p>
                ) : null}
                {requiredPhotosOnTheWay > 0 && !submitted ? (
                    <p className="m-0 mt-1 text-[14px] leading-[1.5] text-[var(--warn-text)]">
                        {copy.photosOnTheWay(requiredPhotosOnTheWay)}
                    </p>
                ) : null}
                {report.questionsUnanswered > 0 && !submitted ? (
                    <p className="m-0 mt-1 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                        {copy.openStaysOut(report.questionsUnanswered)}
                    </p>
                ) : null}
            </Card>

            <Card padding="none" clip>
                <h2 className="m-0 border-b border-[var(--border-subtle)] px-4 py-3 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                    {copy.openTitle}
                </h2>
                {report.nodes.length === 0 ? (
                    <p className="m-0 px-4 py-[14px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                        {copy.nothingOpen}
                    </p>
                ) : (
                    <ul className="m-0 list-none p-0">
                        {report.nodes.map((node) => (
                            <li
                                key={node.nodeId}
                                className="border-b border-[var(--border-subtle)] last:border-b-0"
                            >
                                <button
                                    type="button"
                                    onClick={() => onOpenNode(node.nodeId)}
                                    className="flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 border-0 bg-[var(--surface-card)] px-4 py-[10px] text-left font-[family-name:var(--font-body)] outline-offset-[-2px] hover:bg-[var(--surface-detail)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                                >
                                    <span className="min-w-0 text-[14px] leading-[1.35] text-[var(--text-primary)]">
                                        {node.place}
                                    </span>
                                    <span className="shrink-0 text-[12px] text-[var(--text-tertiary-safe)]">
                                        {copy.nodeOpen(node.unanswered, node.photosMissing)}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>

            {report.amountsMissing.length > 0 ? (
                <Card padding="none" clip data-testid="amounts-missing">
                    <div className="border-b border-[var(--border-subtle)] px-4 py-3">
                        <h2 className="m-0 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                            {copy.amountsTitle}
                        </h2>
                        <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                            {copy.amountsExplanation}
                        </p>
                    </div>
                    <ul className="m-0 list-none p-0">
                        {report.amountsMissing.map((amount) => (
                            <li
                                key={amount.key}
                                className="border-b border-[var(--border-subtle)] last:border-b-0"
                            >
                                <button
                                    type="button"
                                    onClick={() => onOpenNode(amount.nodeId)}
                                    className="flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-3 border-0 bg-[var(--surface-card)] px-4 py-[10px] text-left font-[family-name:var(--font-body)] outline-offset-[-2px] hover:bg-[var(--surface-detail)] focus-visible:outline-2 focus-visible:outline-[var(--accent)]"
                                >
                                    <span className="min-w-0 text-[14px] leading-[1.35] text-[var(--text-primary)]">
                                        {amount.place}
                                    </span>
                                    <span className="min-w-0 text-right text-[12px] text-[var(--warn-text)]">
                                        {amount.label}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Card>
            ) : null}

            {photoQueue.length > 0 ? (
                <PhotoQueueCard photos={photoQueue} onRetry={onRetryPhoto} onSkip={onSkipPhoto} />
            ) : null}

            {error ? (
                <Card variant="warn" padding="md" role="status">
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                        {error}
                    </p>
                </Card>
            ) : null}

            {submitted ? null : (
                <Card padding="md">
                    <div className="flex flex-col gap-[14px]">
                        {status === 'completed' ? null : (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <p className="m-0 max-w-[460px] text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                                    {copy.completeExplanation}
                                </p>
                                <Button
                                    variant="dark"
                                    size="md"
                                    disabled={busy !== null}
                                    onClick={onComplete}
                                >
                                    {copy.complete}
                                </Button>
                            </div>
                        )}

                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <p className="m-0 max-w-[460px] text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                                {!online
                                    ? copy.submitNeedsConnection
                                    : pending.operations > 0
                                      ? copy.submitNeedsSync(pending.operations)
                                      : pending.photos > 0
                                        ? copy.submitWithPhotosOnTheWay(pending.photos)
                                        : copy.submitExplanation}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {online && pending.operations + pending.photos > 0 ? (
                                    <Button variant="outline" size="md" onClick={onSyncNow}>
                                        {copy.syncNow}
                                    </Button>
                                ) : null}
                                <Button
                                    variant="primary"
                                    size="md"
                                    disabled={!canSubmit}
                                    aria-busy={busy === 'submit'}
                                    onClick={onSubmit}
                                >
                                    {busy === 'submit' ? copy.submitting : copy.submit}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            <div>
                <Button variant="outline" size="md" onClick={onClose}>
                    {copy.backToSurvey}
                </Button>
            </div>
        </div>
    );
}

/**
 * Per foto wat er speelt, in plaats van alleen een aantal: een foto die blijft
 * haperen zegt waarom, met "Opnieuw proberen" en - waar wachten niets meer
 * oplevert - "Overslaan". Amber, nooit rood: de foto staat veilig op het apparaat.
 */
function PhotoQueueCard({
    photos,
    onRetry,
    onSkip,
}: {
    photos: readonly QueuedPhoto[];
    onRetry: (clientUuid: string) => void;
    onSkip: (clientUuid: string) => void;
}) {
    const copy = surveyCopy.completion;

    return (
        <Card padding="none" clip data-testid="photo-queue">
            <div className="border-b border-[var(--border-subtle)] px-4 py-3">
                <h2 className="m-0 font-[family-name:var(--font-display)] text-[15px] font-semibold text-[var(--text-primary)]">
                    {copy.photoQueueTitle(photos.length)}
                </h2>
                <p className="m-0 mt-1 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                    {copy.photoQueueExplanation}
                </p>
            </div>
            <ul className="m-0 list-none p-0">
                {photos.map((photo) => {
                    const trouble =
                        photo.status.kind === 'retrying' ||
                        photo.status.kind === 'refused' ||
                        photo.status.kind === 'withoutAnswer';

                    return (
                        <li
                            key={photo.clientUuid}
                            data-photo-status={photo.status.kind}
                            className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border-subtle)] px-4 py-[10px] last:border-b-0"
                        >
                            <div className="min-w-0 flex-1">
                                <p className="m-0 text-[14px] leading-[1.35] text-[var(--text-primary)]">
                                    {photo.caption || photo.fileName}
                                    {photo.caption ? (
                                        <span className="text-[var(--text-tertiary-safe)]">
                                            {` · ${photo.fileName}`}
                                        </span>
                                    ) : null}
                                </p>
                                <p
                                    className={
                                        trouble
                                            ? 'm-0 mt-[2px] text-[12px] leading-[1.4] text-[var(--warn-text)]'
                                            : 'm-0 mt-[2px] text-[12px] leading-[1.4] text-[var(--text-secondary)]'
                                    }
                                >
                                    {photoStatusText(photo)}
                                </p>
                            </div>
                            {photo.canRetry || photo.canSkip ? (
                                <div className="flex flex-wrap gap-2">
                                    {photo.canRetry ? (
                                        <Button
                                            variant="outline"
                                            size="md"
                                            aria-label={copy.photoRetryLabel(
                                                photo.caption || photo.fileName,
                                            )}
                                            onClick={() => onRetry(photo.clientUuid)}
                                        >
                                            {copy.photoRetry}
                                        </Button>
                                    ) : null}
                                    {photo.canSkip ? (
                                        <Button
                                            variant="outline"
                                            size="md"
                                            aria-label={copy.photoSkipLabel(
                                                photo.caption || photo.fileName,
                                            )}
                                            onClick={() => onSkip(photo.clientUuid)}
                                        >
                                            {copy.photoSkip}
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
}

function photoStatusText({ status }: QueuedPhoto): string {
    const copy = surveyCopy.completion.photoStatus;

    switch (status.kind) {
        case 'retrying':
            return copy.retrying(status.error, formatTime(status.nextAttemptAt));
        case 'refused':
            return copy.refused(status.error);
        default:
            return copy[status.kind];
    }
}
